import { useCallback, useEffect, useState } from 'react'
import { Download, Laptop, RefreshCw, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@orisini/ui/components/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@orisini/ui/components/card'
import { api, ApiError } from '@orisini/ui/lib/api'
import { cn } from '@orisini/ui/lib/utils'

interface DeviceDto {
  id: string
  name: string
  tokenPrefix: string
  platform: string | null
  agentVersion: string | null
  hostname: string | null
  lastSeenAt: number | null
  connected: boolean
  createdAt: number
}

export default function AgentPage() {
  const [devices, setDevices] = useState<DeviceDto[]>([])
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    try {
      setDevices((await api.get<{ devices: DeviceDto[] }>('/agent/devices')).devices)
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Couldn\'t load devices')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
    const t = setInterval(() => void load(), 20_000)
    return () => clearInterval(t)
  }, [load])

  function onRevoke(d: DeviceDto) {
    toast(`Revoke device "${d.name}"?`, {
      description:
        'Devices belong to the Orisini account, so revoking applies to ALL products — ' +
        'the agent on that computer is disconnected immediately and must be paired again.',
      action: {
        label: 'Revoke',
        onClick: () => {
          api
            .delete(`/agent/devices/${d.id}`)
            .then(() => {
              toast.success('Device revoked')
              return load()
            })
            .catch((err: unknown) =>
              toast.error(err instanceof ApiError ? err.message : 'Couldn\'t revoke'),
            )
        },
      },
    })
  }

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Devices</h1>
          <p className="text-sm text-muted-foreground">
            Desktop app that posts pins from your computer
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()}>
          <RefreshCw />
          Refresh
        </Button>
      </header>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Install the Pinboss app</CardTitle>
          <CardDescription>
            Posting is done by the app on your computer — not by the server — so your Pinterest
            login never leaves that computer.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ol className="list-inside list-decimal space-y-1.5 text-sm text-muted-foreground">
            <li>Download and install the app for your operating system.</li>
            <li>Open the app and sign in with this Pinboss email and password.</li>
            <li>From the tray menu, click "Login Pinterest" for each account and sign in manually once.</li>
            <li>Leave the computer on — pins will be posted on schedule.</li>
          </ol>

          <div className="flex flex-wrap gap-3">
            <Button asChild variant="secondary">
              <a href="/api/agent/release/win/installer" download>
                <Download />
                Windows (.exe)
              </a>
            </Button>
            <Button asChild variant="secondary">
              <a href="/api/agent/release/mac/installer" download>
                <Download />
                macOS (.dmg)
              </a>
            </Button>
          </div>

          <div className="space-y-1.5 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-500">
            <p className="font-medium">Operating system warning during install (normal)</p>
            <p>
              The installer isn't digitally signed, so <strong>Windows SmartScreen</strong> will
              show "Windows protected your PC" — click <em>More info → Run anyway</em>. On{' '}
              <strong>macOS</strong>, right-click the app → <em>Open</em> on first launch.
              Automatic updates on macOS are also unavailable without signing — the app will let
              you know when a new version is available to download manually.
            </p>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : devices.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No devices installed yet. Install the desktop app and sign in from there.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {devices.map((d) => (
            <Card key={d.id}>
              <CardContent className="flex items-center gap-4 p-4">
                <Laptop className="size-8 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate font-medium">{d.name}</p>
                    <span
                      className={cn(
                        'flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium',
                        d.connected
                          ? 'bg-emerald-500/15 text-emerald-400'
                          : 'bg-muted text-muted-foreground',
                      )}
                    >
                      <span
                        className={cn(
                          'size-1.5 rounded-full',
                          d.connected ? 'bg-emerald-400' : 'bg-muted-foreground',
                        )}
                      />
                      {d.connected ? 'Connected' : 'Offline'}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {d.hostname ?? '—'} · {d.platform ?? '?'} · v{d.agentVersion ?? '?'} · token{' '}
                    {d.tokenPrefix}…
                    {d.lastSeenAt && (
                      <>
                        {' '}
                        · last active{' '}
                        {new Date(d.lastSeenAt).toLocaleString('en-GB', {
                          dateStyle: 'short',
                          timeStyle: 'short',
                        })}
                      </>
                    )}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="shrink-0 text-muted-foreground hover:text-destructive"
                  title="Revoke device"
                  onClick={() => onRevoke(d)}
                >
                  <Trash2 className="size-4" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
